/**
 * @fileoverview Present lightweight batch context before staff open a conversation
 * feed. Communications owns selection and unsaved-edit admission; this picker
 * searches summaries without loading drafts. Counts describe recorded drafts,
 * contact and replies rather than permission to send an email.
 */
import React from 'react';
import AppDropdown from '../shared/AppDropdown';
import AppIcon from '../shared/AppIcon';
import { titleize } from './intakeFormatters';

const date = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const period = (option) => {
  const start = date(option.opened_at), end = date(option.closed_at);
  return start ? end && end !== start ? `${start} – ${end}` : start : 'Not assigned to a batch';
};
const count = (number, singular, plural = `${singular}s`) => `${number} ${number === 1 ? singular : plural}`;

function BatchContext({ option, compact = false }) {
  if (!option) return <span>Choose a batch</span>;
  const hasDraftCount = Number.isFinite(option.draft_count);
  return <div className={`communications-batch-choice${compact ? ' communications-batch-choice--compact' : ''}`}>
    <div className="communications-batch-choice__title"><strong>{option.label}</strong>
      {hasDraftCount && <span className={`communications-batch-choice__drafts${option.draft_count ? ' communications-batch-choice__drafts--active' : ''}`}><AppIcon icon="pi pi-file-edit" />{count(option.draft_count, 'draft')}</span>}
    </div>
    <span className="communications-batch-choice__dates"><AppIcon icon="pi pi-calendar" />{period(option)}{option.status === 'open' && <span className="communications-batch-choice__collecting">Collecting leads</span>}</span>
    <div className="communications-batch-choice__counts">
      <span>{count(option.total, 'conversation')}</span>
      {!compact && option.lead_count > 0 && <span>{option.lead_count} leads in batch</span>}
      {!compact && option.contacted_count > 0 && <span>{option.contacted_count} contacted</span>}
      {!compact && option.reply_count > 0 && <span>{option.reply_count} with replies</span>}
    </div>
    {!compact && option.categories?.length > 0 && <div className="communications-batch-choice__topics">{option.categories.map((topic) => <span key={topic.name}>{titleize(topic.name)} <small>{topic.count}</small></span>)}</div>}
  </div>;
}

export default function CommunicationBatchPicker({ options, value, disabled, onChange }) {
  const index = options.findIndex((option) => option.key === value);
  const searchable = options.map((option) => ({ ...option, searchText: [option.label, `#${option.id || ''}`, period(option), ...(option.categories || []).map((topic) => titleize(topic.name))].join(' ') }));
  return <div className="communications-batch-picker">
    <label htmlFor="communication-batch">Batch</label>
    <div className="communications-batch-picker__controls">
      <AppDropdown inputId="communication-batch" aria-label="Communication batch" className="communications-batch-dropdown" panelClassName="communications-batch-panel"
        value={searchable.find((option) => option.key === value) || null} options={searchable} optionLabel="label" disabled={disabled || !options.length}
        onChange={(event) => { if (event.value) onChange(event.value.key); }} filter filterBy="searchText" filterPlaceholder="Find a batch by number, date or category"
        filterInputProps={{ 'aria-label': 'Find a batch' }} scrollHeight="24rem" emptyFilterMessage="No batches match your search."
        valueTemplate={(option) => <BatchContext option={option} compact />} itemTemplate={(option) => <BatchContext option={option} />} />
      <div className="communications-batch-picker__navigation" role="group" aria-label="Batch navigation">
        <button type="button" className="communications-icon-button" aria-label="Previous batch" disabled={disabled || index <= 0} onClick={() => onChange(options[index - 1].key)}><AppIcon icon="pi pi-chevron-left" /></button>
        <span aria-live="polite">{index < 0 ? 0 : index + 1} / {options.length}</span>
        <button type="button" className="communications-icon-button" aria-label="Next batch" disabled={disabled || index < 0 || index >= options.length - 1} onClick={() => onChange(options[index + 1].key)}><AppIcon icon="pi pi-chevron-right" /></button>
      </div>
    </div>
  </div>;
}
